import { Helmet } from 'react-helmet-async';

interface StructuredDataProps {
  data: Record<string, any> | Record<string, any>[];
}

export function StructuredData({ data }: StructuredDataProps) {
  const items = Array.isArray(data) ? data : [data];
  return (
    <Helmet>
      {items.map((item, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}

export function buildWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'TrustROI',
    url: 'https://trustroi.online',
    description: 'Best software tools ranked by ROI — compare reviews, unbiased tool rankings, and software ROI comparison using verified users, retention, and real pricing.',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://trustroi.online/?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
}

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'TrustROI',
    url: 'https://trustroi.online',
    logo: 'https://trustroi.online/favicon.png',
    description: 'Software ROI Intelligence Platform — compare tools by payment-gateway-verified data, not reviews or upvotes.',
    sameAs: [
      'https://www.linkedin.com/company/trustroi/',
      'https://x.com/trustroi_online',
      'https://www.reddit.com/r/TrustROI/',
    ],
  };
}

export function buildSiteNavigationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SiteNavigationElement',
    name: 'TrustROI Navigation',
    hasPart: [
      { '@type': 'WebPage', name: 'ROI Leaderboard', url: 'https://trustroi.online/' },
      { '@type': 'WebPage', name: 'TrustWall — Verified Testimonials', url: 'https://trustroi.online/trustwall' },
      { '@type': 'WebPage', name: 'Smart Stack Builder', url: 'https://trustroi.online/stack-builder' },
      { '@type': 'WebPage', name: 'Browse Categories', url: 'https://trustroi.online/categories' },
      { '@type': 'WebPage', name: 'Add Your Tool', url: 'https://trustroi.online/add' },
      { '@type': 'WebPage', name: 'Blog', url: 'https://trustroi.online/blog' },
      { '@type': 'WebPage', name: 'About TrustROI', url: 'https://trustroi.online/about' },
      { '@type': 'WebPage', name: 'Sponsorship', url: 'https://trustroi.online/sponsorship' },
      { '@type': 'WebPage', name: 'Alternatives', url: 'https://trustroi.online/alternatives' },
    ],
  };
}

export function buildProductListSchema(products: Array<{ name: string; slug: string; description: string; logo_url: string; pricing_monthly: number; category: string; trust_score: number }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Best Software Tools Ranked by ROI — Software ROI Comparison',
    description: 'Best software tools for startups ranked by verified ROI. Unbiased reviews, tool comparison, and highest ROI business software.',
    numberOfItems: products.length,
    itemListElement: products.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'SoftwareApplication',
        name: p.name,
        url: `https://trustroi.online/product/${p.slug}`,
        description: p.description,
        image: p.logo_url,
        applicationCategory: p.category,
        offers: {
          '@type': 'Offer',
          price: p.pricing_monthly,
          priceCurrency: 'USD',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: p.pricing_monthly, priceCurrency: 'USD', billingDuration: 'P1M' },
        },
      },
    })),
  };
}

export function buildBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function buildPersonSchema(person: { name: string; url: string; jobTitle?: string; image?: string; knowsAbout?: string[] }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: person.name,
    url: person.url,
    sameAs: [person.url],
    jobTitle: person.jobTitle || 'Co-founder, TrustROI',
    worksFor: { '@type': 'Organization', name: 'TrustROI', url: 'https://trustroi.online' },
    image: person.image,
    knowsAbout: person.knowsAbout || ['Software ROI', 'SaaS Analytics', 'Payment Verification', 'Customer Retention'],
  };
}

export const TRUSTROI_AUTHORS = [
  {
    name: 'Sam Wilson',
    url: 'https://www.linkedin.com/in/sam-wilson-143985249/',
    jobTitle: 'Co-founder, TrustROI',
    knowsAbout: ['Software ROI', 'SaaS Metrics', 'Customer Retention', 'Payment Gateway Verification'],
  },
  {
    name: 'Rajesh Yadav',
    url: 'https://www.linkedin.com/in/therajeshyadav/',
    jobTitle: 'Co-founder, TrustROI',
    knowsAbout: ['SaaS Analytics', 'Software Comparison', 'Startup Growth', 'Product Verification'],
  },
];

export function buildSoftwareSchema(product: {
  name: string; slug: string; description: string; logo_url: string;
  pricing_monthly: number; category: string; trust_score: number;
  website_url: string; founder_name: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: product.name,
    url: product.website_url,
    description: product.description,
    image: product.logo_url,
    applicationCategory: product.category,
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: product.pricing_monthly,
      priceCurrency: 'USD',
      priceSpecification: { '@type': 'UnitPriceSpecification', price: product.pricing_monthly, priceCurrency: 'USD', billingDuration: 'P1M' },
    },
    author: { '@type': 'Person', name: product.founder_name },
  };
}

export function buildCollectionSchema(categories: Record<string, Array<{ name: string; slug: string; description: string; pricing_monthly: number }>>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Software Tools by Category',
    description: 'Browse and compare software tools organized by category, ranked by payment-gateway-verified data.',
    url: 'https://trustroi.online/categories',
    mainEntity: Object.entries(categories).map(([category, items]) => ({
      '@type': 'ItemList',
      name: `Best ${category} Tools`,
      numberOfItems: items.length,
      itemListElement: items.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: { '@type': 'SoftwareApplication', name: p.name, url: `https://trustroi.online/product/${p.slug}`, description: p.description },
      })),
    })),
  };
}
